/**
 * @fileoverview MiniGraph Component - A simplified wrapper for LineGraph component
 *
 * This file contains the MiniGraph component which provides a streamlined interface
 * for creating dual-axis line charts with automatic tick generation. It's primarily
 * used for displaying workout statistics, fitness data, and other time-series data
 * in a compact format.
 *
 * Key features:
 * - Automatic x-axis tick generation based on data density
 * - Dual y-axis support for comparing different metrics
 * - Built-in internationalization support
 * - Configurable smoothing and averaging
 * - Debug mode for development
 *
 * @author KeepSpot Development Team
 * @since 2023
 */

import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text, Localizer, withText } from 'preact-i18n';

import mu from '../../lib/util';

import LineGraph from '../../components/lineGraph';

/**
 * MiniGraph Component
 *
 * A compact wrapper around the LineGraph component that provides a simplified interface
 * for displaying dual-axis line charts with automatic x-axis tick generation.
 *
 * Features:
 * - Dual-axis support (left and right y-axes)
 * - Automatic x-axis tick generation based on data length
 * - Configurable smoothing and averaging windows
 * - Debug mode for data inspection
 * - Internationalization support for graph labels
 * - Flexible data format: accepts arrays of objects with {epoch, x, y} structure
 *
 * @component
 * @example
 * // Basic usage with animal weight data
 * <MiniGraph
 *   stores={this.props.stores}
 *   dataLeft={[animal.allWeights.map(d => ({
 *     epoch: Math.floor(new Date(d.date).getTime() / 1000),
 *     x: Math.floor(new Date(d.date).getTime() / 1000),
 *     y: d.weight
 *   }))]}
 *   legendLeft={['Weight']}
 * />
 *
 * @example
 * // Workout distance over time with custom formatting
 * <MiniGraph
 *   stores={stores}
 *   dataLeft={[workouts.map(w => ({
 *     epoch: new Date(w.date).getTime(),
 *     x: new Date(w.date).getTime(),
 *     y: w.distance
 *   }))]}
 *   legendLeft={['Distance (km)']}
 *   xTicksVal={val => new Date(val).toLocaleDateString()}
 *   yTicksLeftVal={val => `${val}km`}
 *   width={600}
 *   height={200}
 * />
 *
 * @example
 * // Dual-axis usage with distance and elevation
 * <MiniGraph
 *   stores={stores}
 *   dataLeft={distanceData}
 *   legendLeft={['Distance (km)']}
 *   dataRight={elevationData}
 *   legendRight={['Elevation (m)']}
 *   fieldsLeft={['epoch', 'distance']}
 *   fieldsRight={['epoch', 'elevation']}
 *   yTicksLeftVal={val => `${val}km`}
 *   yTicksRightVal={val => `${val}m`}
 * />
 */
@withText((props) => ({
    week: <Text id='workout.graph.week' />,
    distance: <Text id='workout.graph.distance' />,
    ascend: <Text id='workout.graph.ascend' />,
    plan: <Text id='workout.graph.plan' />,
}))
@observer
class MiniGraph extends Component {
    /**
     * Creates an instance of MiniGraph.
     *
     * @param {Object} props - Component props
     * @param {Array[]} [props.dataLeft=[]] - Array of data series for the left y-axis. Each series is an array of objects with {epoch, x, y} structure
     * @param {Array[]} [props.dataRight=[]] - Array of data series for the right y-axis. Each series is an array of objects with {epoch, x, y} structure
     * @param {string[]} [props.legendLeft=[]] - Legend labels for left y-axis data series
     * @param {string[]} [props.legendRight=[]] - Legend labels for right y-axis data series
     * @param {string[]} [props.fieldsLeft=[]] - Field names for left y-axis data (currently unused)
     * @param {string[]} [props.fieldsRight=[]] - Field names for right y-axis data (currently unused)
     * @param {Function} [props.xTicksVal] - Function to format x-axis tick values. Defaults to ISO date formatting
     * @param {number} [props.xTicksCnt=5] - Number of x-axis ticks to display
     * @param {Function} [props.yTicksLeftVal] - Function to format left y-axis tick values. Defaults to identity function
     * @param {Function} [props.yTicksRightVal] - Function to format right y-axis tick values. Defaults to identity function
     * @param {number} [props.width=450] - Graph width in pixels
     * @param {number} [props.height=180] - Graph height in pixels
     * @param {boolean} [props.showDebug=false] - Whether to show debug information in console and render data as JSON
     * @param {Object} props.stores - MobX stores passed to the underlying LineGraph component
     */
    constructor(props) {
        super(props);
        this.state = {
        };
    }

    /**
     * Legacy method for processing time-based data into hourly intervals.
     *
     * @deprecated This method is currently commented out and not in use.
     *
     * @description This method was designed to:
     * - Generate hourly time ranges from a starting point (30 days ago) to today
     * - Transform input data with date/qty structure into hourly buckets
     * - Fill gaps in the data by carrying forward the last known value
     * - Create data points with epoch timestamps and sequential x/y coordinates
     *
     * The method would take month/year props and dataLeft to generate
     * a complete hourly dataset suitable for time-series visualization.
     *
     * @example
     * // Would have been called in componentDidMount:
     * // this.setDays();
     *
     * @todo Determine if this functionality should be restored or removed entirely
     */
    // setDays = () => {
    //     const { month, year, dataLeft } = this.props;

    //     const now = new Date();
    //     const priorDate = new Date(new Date().setDate(now.getDate() - 30));
    //     const currentMonth = parseInt(month || priorDate.getMonth() + 1, 10);
    //     const currentYear = parseInt(year || priorDate.getFullYear(), 10);
    //     const currentDay = priorDate.getDate();
    //     const today = util.isoDate(new Date(), false, false, true);
    //     util.padDate();
    //     const hours = util.hourRange(`${currentYear}-${util.padDate(currentMonth)}-${util.padDate(currentDay)}`, `${today}`);
    //     const inputData = [];
    //     dataLeft.forEach((data) => {
    //         const d = new Date(data.date);
    //         inputData.push({
    //             year: d.getFullYear(),
    //             month: d.getMonth() + 1,
    //             day: d.getDate(),
    //             hour: d.getHours(),
    //             qty: data.qty,
    //         });
    //     });

    //     const data = [];
    //     let lastVal = 0;
    //     hours.forEach((date, idx) => {
    //         const yyyy = date.year;
    //         const mm = date.month;
    //         const dd = date.day;
    //         const hh = date.hour;
    //         const hour = inputData.findLast(d => d.year === yyyy && d.month === mm && d.day === dd && d.hour === hh);
    //         lastVal = hour ? hour.qty : lastVal;
    //         const obj = {
    //             epoch: new Date(`${yyyy}-${mm}-${dd} ${hh}:00:00`).getTime(),
    //             x: idx,
    //             y: lastVal,
    //         };
    //         data.push(obj);
    //     });

    //     this.setState({ dataLeft: [data] });
    // }

    /**
     * Legacy lifecycle method that would have initialized data processing.
     *
     * @deprecated This method is currently commented out and not in use.
     *
     * @description Would have called setDays() to process and prepare
     * time-series data when the component first mounted. This was part
     * of the original data processing pipeline that has since been
     * replaced by direct prop-based data passing.
     */
    // componentDidMount() {
    //     this.setDays();
    // }

    /**
     * Renders the mini graph component.
     *
     * The render method performs the following operations:
     * 1. Extracts and destructures props with default values
     * 2. Generates x-axis ticks automatically based on the first data series length
     * 3. Calculates tick positions by dividing the data into equal parts
     * 4. Renders debug information if showDebug is enabled
     * 5. Passes all configuration to the underlying LineGraph component
     *
     * @returns {JSX.Element} The rendered component containing:
     *   - Debug JSON output (if showDebug is true)
     *   - LineGraph component with configured props for dual-axis display
     *
     * @description The x-axis ticks are automatically generated by:
     *   - Taking the first data series from dataLeft
     *   - Dividing it into (xTicksCnt - 1) equal parts
     *   - Using the epoch values from data points as tick positions
     *   - Always including the first and last data points as ticks
     *
     * The right y-axis is conditionally shown only when dataRight has data.
     * Smoothing and averaging windows are pre-configured for optimal display.
     */
    render() {
        const {
            fieldsLeft = [],
            legendLeft = [],
            fieldsRight = [],
            legendRight = [],
            showDebug,
            xTicksVal = val => util.isoDate(val, false, false, true),
            xTicksCnt = 5,
            yTicksLeftVal = val => val,
            yTicksRightVal = val => val,
            dataLeft = [],
            dataRight = [],
            width = 250,
            height = 100,
            showXTicks = 1,
            showYTicksLeft = 1,
            showYTicksRight,
            paddingTop = 7,
            paddingLeft = 30,
            paddingRight,
            yTicksLeftWidth = '30px',
            yTicksRightWidth,
            inputStyleLeft = 'stroke-width: 2px;',
            inputStyleRight = 'stroke-width: 2px; stroke-dasharray: 5, 5;',
            xTicks: customXTicks,
            paddingBottom,
            xTicksStyle,
            displayTypeLeft,
            displayTypeRight,
            barWidth,
            barOpacity,
            smoothLeft = [],
            gradientFillLeft = [],
            gradientFillRight = [],
            gradientOpacity,
            showValueLeftLine = false,
            showValueRightLine = false,
            unitLeft = [],
            unitRight = [],
            hoverValLeft = [],
            hoverValRight = [],
        } = this.props;

        const firstData = dataLeft && dataLeft[0] ? dataLeft[0] : [];
        let xTicks = customXTicks || [];
        if (!customXTicks && firstData.length) {
            const partSize = Math.ceil(firstData.length / (xTicksCnt - 1));
            const parts = Math.floor(firstData.length / partSize);
            if (showDebug) {
                console.log({ firstData, parts, partSize });
            }
            for (let i = 0, l = firstData.length; i < l; i += partSize) {
                xTicks.push(firstData[i].epoch);
            }
            xTicks.push(firstData[firstData.length - 1].epoch);
        }
        return (
            <>
                {/* <xmp>{JSON.stringify(dataLeft, null, 4)}</xmp> */}
                <div class='mt-0 mb-0'>
                    {/* <xmp>graphWorkoutSummaryWeekDistanceCurrent: {JSON.stringify(graphWorkoutSummaryWeekDistanceCurrent, null, 4)}</xmp>
                    <xmp>graphWorkoutSummaryWeekDistancePrev: {JSON.stringify(graphWorkoutSummaryWeekDistancePrev, null, 4)}</xmp> */}
                    <LineGraph
                        stores={this.props.stores}
                        width={width}
                        height={height}
                        showXTicks={showXTicks}
                        xTicksVal={xTicksVal}
                        xTicks={xTicks}
                        paddingTop={paddingTop}
                        paddingLeft={paddingLeft}
                        paddingRight={paddingRight}
                        paddingBottom={paddingBottom}
                        yTicksLeftWidth={yTicksLeftWidth}
                        lineFromXzero={false}
                        lineFromXmax={false}
                        // xTicksLink={'/workouts/week/'}

                        tickCount={3}

                        dataLeft={dataLeft}
                        legendLeft={legendLeft}
                        // yMaxLeft={30}
                        // yMinLeft={0}
                        showYTicksLeft={showYTicksLeft}
                        // yTicksLeftPostfix={'km'}
                        // unitLeft={['km', 'km', 'km']}
                        yTicksLeftVal={yTicksLeftVal}
                        // avgWindowLeft={[2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2]}
                        // smoothLeft={[1, 1, 1, 1, 1, 1, 1, 1, 1, 1]}

                        dataRight={dataRight}
                        legendRight={legendRight}
                        // dataRight={[graphWorkoutSummaryWeekElevationCurrent, graphWorkoutSummaryWeekElevationPrev]}
                        // legendRight={[`${seasonStart} ${ascend}`, `${seasonStart - 1} ${ascend}`]}
                        showYTicksRight={showYTicksRight !== undefined ? showYTicksRight : (dataRight.length > 0 ? 1 : 0)}
                        // yTicksRightPostfix={'m'}
                        // unitRight={['m', 'm']}
                        yTicksRightVal={yTicksRightVal}
                        yTicksRightWidth={yTicksRightWidth}
                        avgWindowRight={[0, 0, 0, 0, 0, 0, 0, 0, 0, 0]}
                        smoothRight={[1, 1, 1, 1, 1, 1, 1, 1, 1, 1]}
                        scrollToRight={true}

                        legendStyle={`font-size: 10px; font-weight: 400;`}
                        xTicksStyle={xTicksStyle || `font-size: 10px; font-weight: 400;`}
                        yTicksLeftStyle={`font-size: 10px; font-weight: 400;`}

                        inputStyleLeft={inputStyleLeft}
                        inputStyleRight={inputStyleRight}

                        displayTypeLeft={displayTypeLeft}
                        displayTypeRight={displayTypeRight}
                        barWidth={barWidth}
                        barOpacity={barOpacity}

                        smoothLeft={smoothLeft}
                        gradientFillLeft={gradientFillLeft}
                        gradientFillRight={gradientFillRight}
                        gradientOpacity={gradientOpacity}

                        showValueLeftLine={showValueLeftLine}
                        showValueRightLine={showValueRightLine}

                        unitLeft={unitLeft}
                        unitRight={unitRight}
                        hoverValLeft={hoverValLeft}
                        hoverValRight={hoverValRight}

                        // marker={week}
                    />
                </div>

            </>
        );
    }
}

export default MiniGraph;
